//recipeImageSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../../services/axiosInstance";
import * as Endpoints from "../../entities/Endpoints";
import { normalizeTitle } from "../../utils/Utils";

export const fetchRecipeImages = createAsyncThunk(
  "recipeImages/fetch",
  async (requestPayload, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.post(
        Endpoints.GET_RECEIPE_IMAGE,
        requestPayload
      );
      return normalizeData(data);
    } catch (error) {
      return rejectWithValue(
        error?.response?.data || "Failed to fetch recipe images"
      );
    }
  }
);

const initialState = {
  items: [],
  byTitle: {},
  loading: false,
  error: null,
};

/** Normalize: API → { items, byTitle } */
const normalizeData = (payload = {}) => {
  const items = Array.isArray(payload.results) ? payload.results : [];

  // items: keep original title for UI/debug, add normalizedTitle
  const normalizedItems = items.map((item) => {
    const title = item.meal || "";
    const normalizedTitle = normalizeTitle(title);
    return {
      title,
      normalizedTitle,
      imageBase64: item.imageBase64 || "",
      imageFileType: "image/png", // default agar backend type nahi bheje
    };
  });

  const byTitle = normalizedItems.reduce((acc, item) => {
    if (item.normalizedTitle) acc[item.normalizedTitle] = item;
    return acc;
  }, {});

  return { items: normalizedItems, byTitle };
};

const recipeImageSlice = createSlice({
  name: "recipeImages",
  initialState,
  reducers: {
    resetRecipeImages: () => initialState,

    upsertRecipeImage: (state, action) => {
      const raw = action.payload || {};
      const title = raw.title || raw.meal || "";
      const normalizedTitle = normalizeTitle(title);
      if (!normalizedTitle) return;

      const item = {
        title,
        normalizedTitle,
        imageBase64: raw.imageBase64 || "",
        imageFileType: raw.imageFileType || "image/png",
        ...raw,
      };

      state.byTitle[normalizedTitle] = item;

      const idx = state.items.findIndex(
        (x) => normalizeTitle(x.title) === normalizedTitle
      );
      if (idx === -1) state.items.push(item);
      else state.items[idx] = item;
    },

    removeRecipeImage: (state, action) => {
      const key = normalizeTitle(action.payload || "");
      if (!key) return;
      delete state.byTitle[key];
      state.items = state.items.filter((x) => normalizeTitle(x.title) !== key);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchRecipeImages.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRecipeImages.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.items;
        state.byTitle = action.payload.byTitle;
      })
      .addCase(fetchRecipeImages.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? "Failed to fetch recipe images";
      });
  },
});

export const { resetRecipeImages, upsertRecipeImage, removeRecipeImage } =
  recipeImageSlice.actions;

export default recipeImageSlice.reducer;

/* -------- Selectors -------- */
export const selectRecipeImagesLoading = (state) => state.recipeImages?.loading;

export const selectRecipeImagesError = (state) => state.recipeImages?.error;

export const selectAllRecipeImages = (state) => state.recipeImages?.items ?? [];

export const selectRecipeImageByTitle = (title) => (state) => {
  const key = normalizeTitle(title || "");
  return state.recipeImages?.byTitle?.[key] ?? null;
};
